// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/shop/components/ShopPrice.module.css';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {
	aznMinorToTryMinor,
	formatAzn,
	formatPayable,
	formatTry,
	formatUsd,
	tryMinorToAznMinor,
	usdCentsToAznMinor,
	usdToAznRateMicro,
} from '@app/features/shop/utils/ShopPriceFormat';
import type {ShopChargeCurrency, ShopViewerPayment} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import type {ShopOrderResponse} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const PAYABLE_DESCRIPTOR = msg({
	message: 'Payable: {amount} AZN',
	comment: 'Exact total charged by Payriff, under the informational TL price.',
});
const MONTHLY_DESCRIPTOR = msg({
	message: '{price} a month',
	comment: 'Monthly TL or USD equivalent of a Micup Plus package; an AZN amount below it is the full package total.',
});
const DAILY_DESCRIPTOR = msg({
	message: '≈ {price} a day',
	comment: 'Informational daily TL or USD equivalent of a Micup Plus package, using 30 days per month.',
});

interface ShopPriceProps {
	cents?: number;
	// Checkout passes the authoritative quote, never recalculates the payable total from catalog settings.
	aznMinor?: number;
	// Discounts subtract rounded cumulative levels in both currencies, so the displayed rows add up.
	subtractAznMinor?: number;
	months?: number;
	primaryOnly?: boolean;
	label?: MessageDescriptor;
	// Once an order exists, use its frozen display quote rather than the current catalog rate.
	displayQuote?: ShopOrderResponse;
	// How this price is charged; the checkout passes its quote's, everything else follows the catalog.
	payment?: ShopViewerPayment | null;
	// `cents` is a server payable amount in this currency's minor unit; null: US cents, converted here. A manat amount
	// shows as Payriff shows it: in TL where the viewer sees TL, with the manat to pay under it.
	currency: ShopChargeCurrency | null;
}

// Reusable by shop cards, hero, plan comparison and checkout; no network calls or timers per price.
export const ShopPrice = observer(
	({
		cents,
		aznMinor,
		subtractAznMinor = 0,
		months,
		primaryOnly = false,
		label,
		displayQuote,
		payment = ShopCatalog.payment,
		currency,
	}: ShopPriceProps) => {
		const {i18n} = useLingui();
		const locale = i18n.locale;
		const paddle = payment?.provider === 'paddle' ? payment.currency : null;
		const own = currency === 'AZN' ? null : currency;
		// Payriff in TL: the admin's lira price, charged as its manat value (the checkout passes the quote's). Payriff
		// charges no lira, so a lira amount on it is always one, whatever currency the catalog showed.
		const payriffLira = own === 'TRY' && payment?.provider === 'payriff';
		// The TL or USD price, which a month divides; without one the AZN amount shows whole.
		let local: {minor: number; format: (minor: number, locale: string) => string} | null = null;
		let azn: number | null = 0;
		if (payriffLira) {
			if (cents == null) return null;
			local = {minor: cents, format: formatTry};
			azn = aznMinor ?? tryMinorToAznMinor(cents, ShopCatalog.settings?.try_to_azn_rate_micro);
		} else if (own) {
			if (cents == null) return null;
			local = {minor: cents, format: (minor, at) => formatPayable(minor, own, at)};
		} else if (!currency && paddle === 'USD') {
			// Paddle charges the USD price itself: no AZN step.
			if (cents == null) return null;
			local = {minor: cents, format: formatUsd};
		} else {
			const settings = ShopCatalog.settings;
			const rate = usdToAznRateMicro(settings);
			const amount =
				aznMinor ?? (cents == null ? null : currency ? cents : rate !== null ? usdCentsToAznMinor(cents, rate) : null);
			if (amount === null) return null;
			const tryRate = displayQuote
				? displayQuote.display_currency === 'TRY'
					? displayQuote.display_rate_micro
					: null
				: settings?.try_to_azn_rate_micro;
			const converted =
				displayQuote?.display_currency === 'TRY' && amount === displayQuote.amount_azn_minor
					? (displayQuote.display_amount_minor ?? null)
					: aznMinorToTryMinor(amount, tryRate);
			const convertedSubtract = subtractAznMinor === 0 ? 0 : aznMinorToTryMinor(subtractAznMinor, tryRate);
			const tryMinor = converted === null || convertedSubtract === null ? null : converted - convertedSubtract;
			// Paddle TRY charges the TL amount: without a TL rate there is no price to show.
			if (tryMinor === null && paddle === 'TRY') return null;
			if (tryMinor !== null) local = {minor: tryMinor, format: formatTry};
			azn = amount - subtractAznMinor;
		}
		const monthly = local === null ? null : Math.round(local.minor / (months ?? 1));
		const primary =
			local === null || monthly === null ? `${formatAzn(azn ?? 0, locale)} AZN` : local.format(monthly, locale);
		const price = months && monthly !== null ? i18n._(MONTHLY_DESCRIPTOR, {price: primary}) : primary;
		return (
			<span className={styles.price} data-flx="shop.shop-price.price">
				<span data-flx="shop.shop-price.primary">{label ? i18n._(label, {price}) : price}</span>
				{!primaryOnly && months && local && monthly !== null && (
					<span className={styles.note} data-flx="shop.shop-price.daily">
						{i18n._(DAILY_DESCRIPTOR, {price: local.format(Math.round(monthly / 30), locale)})}
					</span>
				)}
				{/* Where nobody sells (catalog payment null) there is no provider charge to name. */}
				{!primaryOnly && (!own || payriffLira) && !paddle && local && azn !== null && ShopCatalog.payment !== null && (
					<span className={styles.note} data-flx="shop.shop-price.payable">
						{i18n._(PAYABLE_DESCRIPTOR, {amount: formatAzn(azn, locale)})}
					</span>
				)}
			</span>
		);
	},
);
